import type {
	GetProduct,
	IdWithDisplayName,
	QualityPoint,
} from "@byteflies/odoo-typescript";
import type { ExtendedProduct } from "./erp/manufacturing/OdooOperationsService";
import {
	type Product,
	ProductType,
	type QualityCheck,
	Tracking,
} from "./erp/manufacturing/Product";
import { id, name } from "./erp/odoo/OdooUtils";
import type { Group } from "./Plot/cloudApi";
import {
	isBlank,
	isNotBlank,
	substringAfter,
	substringBefore,
	substringBetween,
} from "./StringUtils";
import {
	WI_4WIRESNAP_TEST_WITH_DEVICE,
	WI_24_HR_TEST,
	WI_24_HR_TEST_SERVICING,
	WI_BATTERY_HEALTH_CHECK,
	WI_BUILD_24H_HOLTER,
	WI_BUILD_24H_STARTER_KIT,
	WI_BUILD_DOCK,
	WI_BUILD_DOT,
	WI_BUILD_NAKED_2SNAP_CRADLE,
	WI_BUILD_NAKED_3SNAP_CRADLE_LEFT,
	WI_BUILD_NAKED_3SNAP_CRADLE_RIGHT,
	WI_BUILD_NAKED_4SNAP_CRADLE,
	WI_BUILD_NAKED_4WIRE_CRADLE,
	WI_BUILD_NAKED_ECG_3SNAP_CRADLE,
	WI_BUILD_NAKED_MOTION_CRADLE,
	WI_BUILD_VITAL_SIGNS_VS1,
	WI_BUILD_VITAL_SIGNS_VS2,
	WI_BUILD_VITAL_SIGNS_VS3,
	WI_BUILD_VITAL_SIGNS_VS4,
	WI_CALIBRATE_DOT,
	WI_CHARGE_SENSOR_DOT,
	WI_CLEAN_CRADLE_CONNECTORS,
	WI_CONFIGURE_ECG_DOTS,
	WI_CONFIGURE_ECG_DOTS_DUAL_CHANNEL,
	WI_CONFIGURE_ECG_DOTS_EPICARE,
	WI_CONFIGURE_EEG_DOTS,
	WI_CONFIGURE_EPICARE_FOCAL_BOX_DOTS,
	WI_CONNECT_DOCK_MOBILE_WIFI,
	WI_CRADLE_VISUAL_INSPECTION,
	WI_CRADLE_VISUAL_INSPECTION_CONNECTORS,
	WI_CRADLE_VISUAL_INSPECTION_CRACKS,
	WI_CRADLE_VISUAL_INSPECTION_POGOPADS,
	WI_CRADLE_VISUAL_INSPECTION_WELD_SEAM,
	WI_DOCK_BAT_TEST,
	WI_DOCK_CHECK_RECORDINGS,
	WI_DOCK_CLEANING,
	WI_DOCK_FIRMWARE_UPDATE,
	WI_DOCK_INTERFACE_TEST,
	WI_DOCK_PROVISIONING,
	WI_DOCK_TEST,
	WI_DOCK_VISUAL_INSPECTION,
	WI_DOCK_VISUAL_INSPECTION_BUMPER_FEET,
	WI_DOCK_VISUAL_INSPECTION_CASING,
	WI_DOCK_VISUAL_INSPECTION_INTERFACE,
	WI_DOCK_VISUAL_INSPECTION_SCREWS,
	WI_DOT_EXTERNAL_CLEANING,
	WI_DOT_SIGNAL_QUALITY,
	WI_DOT_VISUAL_INSPECTION,
	WI_DOT_VISUAL_INSPECTION_CRACKS,
	WI_DOT_VISUAL_INSPECTION_POGOPINS,
	WI_DOT_VISUAL_INSPECTION_WELD_SEAM,
	WI_FACTORY_RESET,
	WI_LINE_CLEARANCE_END,
	WI_LINE_CLEARANCE_START,
	WI_PACKAGE_2SNAP_CRADLE,
	WI_PACKAGE_3SNAP_CRADLE_LEFT,
	WI_PACKAGE_3SNAP_CRADLE_RIGHT,
	WI_PACKAGE_4SNAP_CRADLE,
	WI_PACKAGE_4WIRE_CRADLE,
	WI_PACKAGE_ADHESIVE,
	WI_PACKAGE_CARDIO_BOX,
	WI_PACKAGE_CARDIO_MULTI_LEAD_LINEAR_BOX,
	WI_PACKAGE_DOT,
	WI_PACKAGE_ECG_ADHESIVE,
	WI_PACKAGE_EEG_ADHESIVE,
	WI_PACKAGE_EPICARE_ABSCENCE_BOX,
	WI_PAIR_AD_MEDICAL_UA656BLE,
	WI_PRINT_LABEL,
	WI_PRINT_LABEL_DOCK_SERVICING,
	WI_ROUTER_TURN_OFF,
	WI_SENSOR_DOT_HW_TEST,
	WI_SENSOR_DOT_UPLOAD_RECORDINGS,
	WI_TEST_2SNAP_CRADLE,
	WI_TEST_3SNAP_CRADLE_LEFT,
	WI_TEST_3SNAP_CRADLE_RIGHT,
	WI_TEST_4SNAP_CRADLE,
	WI_TEST_4WIRE_CRADLE,
	WI_TEST_4WIRE_CRADLE_WITH_FILTER,
	WI_TEST_CRADLE_RESISTANCES,
	WI_TEST_MOTION_CRADLE,
	WI_THIRD_PARTY_CHECK_ACCESSORIES,
	WI_THIRD_PARTY_CHECK_BATTERY,
	WI_THIRD_PARTY_DEVICE_CLEANING,
	WI_THIRD_PARTY_DEVICE_CONFIGURE,
	WI_THIRD_PARTY_DEVICE_FACTORY_RESET,
	WI_THIRD_PARTY_DEVICE_PROPER_FUNCTIONING,
	WI_THIRD_PARTY_DEVICE_VISUAL_INSPECTION,
	WI_THIRD_PARTY_PROTECTIVE_PACKAGING,
	WI_THIRD_PARTY_READABILITY_LABEL,
	WI_VISUAL_INSPECTION,
	WI_WIFI_TEST,
	WI_WRITE_NFC_TAG,
} from "./WorkInstructions";

export const BF_NAKED_DOT = "BF13000178";
export const GTIN_SENSOR_DOT = "05430001718003";

export const BF_PACKAGED_DOT = "BF13000345";
export const BF_NAKED_DOCK = "BF13000179";
export const GTIN_DOCKING_STATION = "05430001718010";
export const BF_NAKED_4WIRE_CRADLE = "BF13000180";
export const BF_PACKAGED_4WIRE_CRADLE = "BF13000429";
export const BF_NAKED_MOTION_CRADLE = "BF13000367";

export const BF_NAKED_2SNAPCRADLE = "BF13000249";
export const BF_PACKAGED_2SNAPCRADLE = "BF13000339";
export const BF_PACKAGED_ECG_3SNAPCRADLE = "BF13000498";
export const GTIN_2SNAPCRADLE = "05430001718041";
export const GTIN_ECG_3SNAPCRADLE = "05430001718256";

export const BF_NAKED_3SNAPCRADLE_LEFT = "BF13000353";
export const BF_PACKAGED_3SNAPCRADLE_LEFT = "BF13000354";
export const BF_NAKED_3SNAPCRADLE_RIGHT = "BF13000351";
export const BF_PACKAGED_3SNAPCRADLE_RIGHT = "BF13000352";
export const BF_NAKED_4SNAPCRADLE = "BF13000325";
export const BF_PACKAGED_4SNAPCRADLE = "BF13000341";
export const BF_PACKAGED_MOTION_CRADLE = "BF13000340";
export const BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX = "BF13000503";
export const BF_NAKED_2SNAPCRADLE_110 = "BF13000382";
export const BF_NAKED_ECG_3SNAPCRADLE = "BF13000497";
export const BF_NAKED_3WIRECRADLE_LEFT = "BF13000513";
export const BF_NAKED_3WIRECRADLE_RIGHT = "BF13000512";
export const BF_PACKAGED_3WIRECRADLE_LEFT = "BF13000515";
export const BF_PACKAGED_3WIRECRADLE_RIGHT = "BF13000514";

export const BF_DOUBLE_SIDED_ADHESIVE = "BF13000144";
export const BF_PACKAGED_DOUBLE_SIDED_ADHESIVE = "BF13000368";

export const BF_NAKED_ECG_ADHESIVE = "BF13000248";
export const BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR = "BF13000490";
export const BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_110 = "BF24000731";
export const BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA = "BF13000587";
export const BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_110 = "BF24000748";
export const BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD = "BF13000430";
export const BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS = "BF13000526";
export const BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR = "BF13000491";
export const BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS = "BF13000525";
export const BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110 = "BF24000732";
export const BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS = "BF24000735";
export const BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110 = "BF24000749";

export const BF_NAKED_EEG_ADHESIVE_LEFT = "BF13000531";
export const BF_NAKED_EEG_ADHESIVE_RIGHT = "BF13000532";
export const BF_PACKAGED_EEG_ADHESIVE = "BF13000431";
export const BF_NAKED_EEG_ADHESIVE_LEFT_114 = "BF24000805";
export const BF_NAKED_EEG_ADHESIVE_RIGHT_114 = "BF24000806";
export const BF_PACKAGED_EEG_ADHESIVE_114 = "BF24000820";

export const BF_POWER_SUPPLY = "BF13000165";
export const BF_USB_CABLE_1M = "BF13000004";
export const BF_USB_CABLE_1_5M = "BF13000388";
export const BF_XCARE_BOX = "BF13000350";

export const BF_CARDIOCARE_PATIENT_GUIDE_NL = "BF13000427";
export const BF_CARDIOCARE_PATIENT_GUIDE_FR = "BF13000440";
export const BF_CARDIOCARE_PATIENT_DIARY_NL = "BF13000453";
export const BF_CARDIOCARE_PATIENT_DIARY_FR = "BF13000454";
export const BF_CARDIOCARE_MULTI_LEAD_LINEAR_PATIENT_GUIDE_NL = "BF13000505";
export const BF_CARDIOCARE_MULTI_LEAD_LINEAR_PATIENT_GUIDE_FR = "BF13000506";
export const BF_CARDIOCARE_MULTI_LEAD_LINEAR_PATIENT_DIARY_NL = "BF13000507";
export const BF_CARDIOCARE_MULTI_LEAD_LINEAR_PATIENT_DIARY_FR = "BF13000508";
export const BF_CARDIOCARE_PATIENT_IDCARD_NL = "BF13000455";
export const BF_CARDIOCARE_PATIENT_IDCARD_FR = "BF13000456";
export const BF_CARDIOCARE_LINEAR_BE_24H_HOLTER = "BF13000537";
export const BF_CARDIOCARE_LINEAR_INTL_24H_HOLTER = "BF24000839";
export const BF_CARDIOCARE_LINEAR_FR_24H_HOLTER = "BF24000840";
export const BF_CARDIOCARE_NON_LINEAR_BE_24H_HOLTER = "BF13000616";
export const BF_CARDIOCARE_NON_LINEAR_INTL_24H_HOLTER = "BF24000841";
export const BF_CARDIOCARE_NON_LINEAR_FR_24H_HOLTER = "BF24000842";
export const BF_CARDIOCARE_24H_STARTER_KIT = "BF13000542";
export const BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER = "BF13000597";

export const BF_VITAL_SIGNS_VS1 = "BF13000545";
export const BF_VITAL_SIGNS_VS2 = "BF13000570";
export const BF_VITAL_SIGNS_VS3 = "BF13000571";
export const BF_VITAL_SIGNS_VS4 = "BF13000572";
export const BF_VITAL_SIGNS_VS6 = "BF24000715";
export const BF_VITAL_SIGNS_VS7 = "BF24000827";
export const BF_VITAL_SIGNS_VS8 = "BF24000730";
export const BF_VITAL_SIGNS_VS9 = "BF24000793";

export const BF_BLOOD_PRESSURE_MONITOR = "BF13000550";
export const BF_PULSE_OXIMETER = "BF13000563";
export const BF_THERMOMETER = "BF13000560";
export const BF_WEIGHING_SCALE = "BF13000549";

export const BF_GLINET_GLX750_PSU = "BF24000803";

export const MANUFACTURING_GROUP: Group = {
	id: "2f355a60-4c03-11ea-9f2d-0949ce20b25c",
	name: "Manufacturing",
};

export interface DeviceDescription {
	udi?: string;
	gtin?: string;
	serial?: string;
	deviceId?: string;
	versionNumber?: string;
	productionDate?: Date;
}

export const getProductVersion = (product: Product): string | undefined => {
	if (product === undefined) {
		return undefined;
	}

	if (product.name === undefined) {
		return undefined;
	}

	const version =
		product.name.indexOf(" - ") !== -1
			? substringAfter(product.name, " - ")
			: undefined;
	if (version !== undefined) {
		return version;
	}

	const lastSpace = product.name.lastIndexOf(" ");
	const lastDot = product.name.lastIndexOf(".");
	if (lastSpace !== -1 && lastDot !== -1 && lastSpace < lastDot) {
		const version = product.name.substring(lastSpace + 1).trim();
		const dot1 = version.indexOf(".");
		const dot2 = version.lastIndexOf(".");
		if (dot1 !== -1 && dot2 !== -1 && dot1 < dot2) {
			return version;
		}
	}
	return undefined;
};

export const getProductNameWithoutInternalReference = (
	name: string,
): string | undefined => {
	if (name !== undefined && name.indexOf("[") === 0 && name.indexOf("] ") > 1) {
		return substringAfter(name, "] ");
	}
	return name;
};

export function getProductNameWithoutInternalReferenceFromId(
	product_id: IdWithDisplayName,
): string | undefined {
	const name2 = name(product_id);
	if (name2 !== undefined) {
		const open = name2.indexOf("[");
		const close = name2.indexOf("] ");
		if (open !== -1 && close !== -1 && open < close) {
			return name2.substring(close + 2).trim();
		}
	}
	return undefined;
}

export const getProductInternalReferenceFromName = (
	name: string,
): string | undefined => {
	if (name !== undefined && name.indexOf("[") === 0 && name.indexOf("]") > 1) {
		return substringBetween(name, "[", "]");
	}
	return undefined;
};

export const isTrackedByUniqueSerial = (product: GetProduct): boolean => {
	const tracking = product.tracking;
	if (tracking !== undefined) {
		return tracking === "serial";
	}

	if (
		product.default_code === BF_NAKED_2SNAPCRADLE ||
		product.default_code === BF_NAKED_2SNAPCRADLE_110 ||
		product.default_code === BF_NAKED_3SNAPCRADLE_LEFT ||
		product.default_code === BF_NAKED_3SNAPCRADLE_RIGHT ||
		product.default_code === BF_NAKED_3WIRECRADLE_LEFT ||
		product.default_code === BF_NAKED_3WIRECRADLE_RIGHT ||
		product.default_code === BF_NAKED_4SNAPCRADLE ||
		product.default_code === BF_NAKED_ECG_3SNAPCRADLE ||
		product.default_code === BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX ||
		product.default_code === BF_NAKED_DOT ||
		product.default_code === BF_NAKED_DOCK ||
		product.default_code === BF_CARDIOCARE_LINEAR_BE_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_LINEAR_INTL_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_LINEAR_FR_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_NON_LINEAR_BE_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_NON_LINEAR_INTL_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_NON_LINEAR_FR_24H_HOLTER ||
		product.default_code === BF_CARDIOCARE_24H_STARTER_KIT ||
		product.default_code === BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER ||
		product.default_code === BF_VITAL_SIGNS_VS1 ||
		product.default_code === BF_VITAL_SIGNS_VS2 ||
		product.default_code === BF_VITAL_SIGNS_VS3 ||
		product.default_code === BF_VITAL_SIGNS_VS4 ||
		product.default_code === BF_VITAL_SIGNS_VS6 ||
		product.default_code === BF_VITAL_SIGNS_VS7 ||
		product.default_code === BF_VITAL_SIGNS_VS8 ||
		product.default_code === BF_VITAL_SIGNS_VS9 ||
		product.default_code === BF_BLOOD_PRESSURE_MONITOR ||
		product.default_code === BF_PULSE_OXIMETER ||
		product.default_code === BF_THERMOMETER ||
		product.default_code === BF_WEIGHING_SCALE
	) {
		return true;
	}
	return product.tracking === "serial";
};

export const productRefToRoutecardProduct = (
	product_id: IdWithDisplayName,
): ExtendedProduct => {
	const pname = getProductNameWithoutInternalReference(name(product_id)!);
	const internalRef = getProductInternalReferenceFromName(name(product_id)!);
	const tracking = bfNumberToTracking(internalRef!);
	const pr: ExtendedProduct = {
		id: id(product_id),
		name: pname!,
		internalReference: internalRef,
		inventory: { traceability: { tracking: tracking } },
		type: ProductType.StorableProduct,
	} as ExtendedProduct;
	return pr;
};

export const productToTracking = (product: Product) => {
	if (product === undefined) {
		throw new Error("Product is undefined");
	}
	const odooTracking = product?.inventory?.traceability?.tracking;
	if (odooTracking !== undefined) {
		return odooTracking;
	}

	const bfNumber = product?.internalReference;
	if (isNotBlank(bfNumber)) {
		for (const product of products) {
			const internalReferences = product.internalReference;
			for (const internalReference of internalReferences) {
				if (internalReference === bfNumber && product.tracking !== undefined) {
					return product.tracking;
				}
			}
		}

		const tracking = bfNumberToTracking(bfNumber);
		if (tracking !== undefined) {
			return tracking;
		}
	}
	return undefined;
};

const bfNumberToTracking = (bfNumber: string): Tracking => {
	switch (bfNumber) {
		case BF_NAKED_DOT:
		case BF_PACKAGED_DOT:
		case BF_NAKED_DOCK:
		case "BF13000342": // old BF number of byteflies kit
		case BF_NAKED_4WIRE_CRADLE:
		case "BF13000347": // old BF number of Packaged ExG Cradle / 4-wire cradle
		case BF_PACKAGED_2SNAPCRADLE:
		case BF_PACKAGED_4WIRE_CRADLE:
		case BF_NAKED_2SNAPCRADLE:
		case BF_NAKED_2SNAPCRADLE_110:
		case BF_NAKED_3SNAPCRADLE_LEFT:
		case BF_NAKED_ECG_3SNAPCRADLE:
		case BF_PACKAGED_3SNAPCRADLE_LEFT:
		case BF_NAKED_3SNAPCRADLE_RIGHT:
		case BF_PACKAGED_3SNAPCRADLE_RIGHT:
		case BF_NAKED_4SNAPCRADLE:
		case BF_PACKAGED_4SNAPCRADLE:
		case BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX:
		case BF_CARDIOCARE_24H_STARTER_KIT:
		case BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER:
		case BF_NAKED_3WIRECRADLE_LEFT:
		case BF_PACKAGED_3WIRECRADLE_LEFT:
		case BF_NAKED_3WIRECRADLE_RIGHT:
		case BF_PACKAGED_3WIRECRADLE_RIGHT:
		case BF_CARDIOCARE_LINEAR_BE_24H_HOLTER:
		case BF_CARDIOCARE_LINEAR_INTL_24H_HOLTER:
		case BF_CARDIOCARE_LINEAR_FR_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_BE_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_INTL_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_FR_24H_HOLTER:
		case BF_VITAL_SIGNS_VS1:
		case BF_VITAL_SIGNS_VS2:
		case BF_VITAL_SIGNS_VS3:
		case BF_VITAL_SIGNS_VS4:
		case BF_VITAL_SIGNS_VS6:
		case BF_VITAL_SIGNS_VS7:
		case BF_VITAL_SIGNS_VS8:
		case BF_VITAL_SIGNS_VS9:
		case BF_BLOOD_PRESSURE_MONITOR:
		case BF_PULSE_OXIMETER:
		case BF_THERMOMETER:
		case BF_WEIGHING_SCALE:
			return Tracking.ByUniqueSerialNumber;
		default:
			return Tracking.ByLots;
	}
};

export interface ProductDescription {
	name?: string;
	gtin: string;
	internalReference: string[];
	tracking: Tracking;
}

export const isCE = (bfNumber: string, vn: string) => {
	switch (bfNumber) {
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS:
			return true;
		case BF_PACKAGED_EEG_ADHESIVE:
		case BF_PACKAGED_EEG_ADHESIVE_114:
			return vn.toLowerCase() === "4b" ? false : true;
		case BF_PACKAGED_3SNAPCRADLE_LEFT:
		case BF_PACKAGED_3SNAPCRADLE_RIGHT:
		case BF_PACKAGED_4SNAPCRADLE:
		case BF_PACKAGED_4WIRE_CRADLE:
		case BF_PACKAGED_MOTION_CRADLE:
			// switch (vn) {
			//   case "1.1.0":
			//   case "1.2.0":
			//     return false;
			//   default:
			return true;
		//}
		default:
			return true;
	}
};

export const products: ProductDescription[] = [
	{
		name: "Docking Station",
		gtin: "05430001718010",
		internalReference: [BF_NAKED_DOCK],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "4-wire cradle",
		gtin: "05430001718027",
		internalReference: [BF_PACKAGED_4WIRE_CRADLE, BF_NAKED_4WIRE_CRADLE],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Sensor Dot",
		gtin: GTIN_SENSOR_DOT,
		internalReference: [BF_NAKED_DOT, BF_PACKAGED_DOT],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "2-snap cradle",
		gtin: GTIN_2SNAPCRADLE,
		internalReference: [
			BF_NAKED_2SNAPCRADLE,
			BF_NAKED_2SNAPCRADLE_110,
			BF_PACKAGED_2SNAPCRADLE,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "ECG 3-snap cradle",
		gtin: GTIN_ECG_3SNAPCRADLE,
		internalReference: [BF_NAKED_ECG_3SNAPCRADLE, BF_PACKAGED_ECG_3SNAPCRADLE],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "3-snap cradle left",
		gtin: "05430001718126",
		internalReference: [
			BF_NAKED_3SNAPCRADLE_LEFT,
			BF_PACKAGED_3SNAPCRADLE_LEFT,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "3-snap cradle right",
		gtin: "05430001718133",
		internalReference: [
			BF_NAKED_3SNAPCRADLE_RIGHT,
			BF_PACKAGED_3SNAPCRADLE_RIGHT,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "3-wire cradle left",
		gtin: "05430001718287",
		internalReference: [
			BF_NAKED_3WIRECRADLE_LEFT,
			BF_PACKAGED_3WIRECRADLE_LEFT,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "3-wire cradle right",
		gtin: "05430001718294",
		internalReference: [
			BF_NAKED_3WIRECRADLE_RIGHT,
			BF_PACKAGED_3WIRECRADLE_RIGHT,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "ECG adhesive",
		gtin: "05430001718058",
		internalReference: [
			BF_NAKED_ECG_ADHESIVE,
			BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD,
			BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS,
		],
		tracking: Tracking.ByLots,
	},
	{
		name: "ECG Adhesive Multi Lead Linear",
		gtin: "05430001718263",
		internalReference: [
			BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR,
			BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_110,
			BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR,
			BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS,
			BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110,
		],
		tracking: Tracking.ByLots,
	},
	{
		name: "ECG Adhesive Single Lead Extra",
		gtin: "05430001718362",
		internalReference: [
			BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA,
			BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_110,
			BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS,
			BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110,
		],
		tracking: Tracking.ByLots,
	},
	{
		name: "Motion cradle",
		gtin: "05430001718065",
		internalReference: ["BF13000367"],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Packaged motion cradle",
		gtin: "05430001718065",
		internalReference: [BF_PACKAGED_MOTION_CRADLE],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "EEG Adhesive",
		gtin: "05430001718089",
		internalReference: [
			BF_NAKED_EEG_ADHESIVE_LEFT,
			BF_NAKED_EEG_ADHESIVE_RIGHT,
			BF_PACKAGED_EEG_ADHESIVE,
			BF_NAKED_EEG_ADHESIVE_LEFT_114,
			BF_NAKED_EEG_ADHESIVE_RIGHT_114,
			BF_PACKAGED_EEG_ADHESIVE_114,
		],
		tracking: Tracking.ByLots,
	},
	{
		name: "4-snap cradle",
		gtin: "05430001718119",
		internalReference: [BF_NAKED_4SNAPCRADLE, BF_PACKAGED_4SNAPCRADLE],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "EpiCare@Home box",
		gtin: "05430001718157",
		internalReference: ["BF13000357"],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "EpiCare@Home box focal left",
		gtin: "05430001718164",
		internalReference: ["BF13000358"],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "EpiCare@Home box focal right",
		gtin: "05430001718171",
		internalReference: ["BF13000359"],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Double-sided adhesive",
		gtin: "05430001718140",
		internalReference: [BF_DOUBLE_SIDED_ADHESIVE],
		tracking: Tracking.ByLots,
	},
	{
		name: "Packaged double-sided adhesive",
		gtin: "05430001718140",
		internalReference: [BF_PACKAGED_DOUBLE_SIDED_ADHESIVE],
		tracking: Tracking.ByLots,
	},
	{
		name: "CardioCare@Home box",
		gtin: "05430001718188",
		internalReference: ["BF13000371"],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "CardioCare@Home Multi Lead Box",
		gtin: "05430001718270",
		internalReference: [BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "CardioCare@Home 24h Holter",
		gtin: "05430001718317",
		internalReference: [
			BF_CARDIOCARE_LINEAR_BE_24H_HOLTER,
			BF_CARDIOCARE_LINEAR_INTL_24H_HOLTER,
			BF_CARDIOCARE_LINEAR_FR_24H_HOLTER,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "CardioCare@Home Non-Linear 24h Holter",
		gtin: "05430001718393",
		internalReference: [
			BF_CARDIOCARE_NON_LINEAR_BE_24H_HOLTER,
			BF_CARDIOCARE_NON_LINEAR_INTL_24H_HOLTER,
			BF_CARDIOCARE_NON_LINEAR_FR_24H_HOLTER,
		],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "CardioCare@Home 24h Starter Kit",
		gtin: "05430001718300",
		internalReference: [BF_CARDIOCARE_24H_STARTER_KIT],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "CardioCare@Home 24h Starter Kit with WiFi Router",
		gtin: "05430001718386",
		internalReference: [BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-1",
		gtin: "05430001718324",
		internalReference: [BF_VITAL_SIGNS_VS1],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-2",
		gtin: "05430001718331",
		internalReference: [BF_VITAL_SIGNS_VS2],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-3",
		gtin: "05430001718348",
		internalReference: [BF_VITAL_SIGNS_VS3],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-4",
		gtin: "05430001718355",
		internalReference: [BF_VITAL_SIGNS_VS4],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-6",
		gtin: "05430001718409",
		internalReference: [BF_VITAL_SIGNS_VS6],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-7",
		gtin: "05430001718454",
		internalReference: [BF_VITAL_SIGNS_VS7],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-8",
		gtin: "05430001718416",
		internalReference: [BF_VITAL_SIGNS_VS8],
		tracking: Tracking.ByUniqueSerialNumber,
	},
	{
		name: "Vital Signs VS-9",
		gtin: "05430001718430",
		internalReference: [BF_VITAL_SIGNS_VS9],
		tracking: Tracking.ByUniqueSerialNumber,
	},
];

const getProductBfNumber = (product: Product): string | undefined => {
	if (
		product.internalReference !== undefined &&
		product.internalReference.trim() !== ""
	) {
		return product.internalReference;
	} else if (product.name !== undefined && product.name.startsWith("[")) {
		return substringBetween(product.name, "[", "]");
	} else {
		return undefined;
	}
};

export const productToGtin = (product: Product): string => {
	const intRef = getProductBfNumber(product);
	if (intRef === undefined || intRef === "") {
		throw new Error(
			`Unable to find registry type for ${product.name} (No internal reference number)`,
		);
	}
	for (const product of products) {
		for (const r of product.internalReference) {
			if (r === intRef) {
				return product.gtin;
			}
		}
	}
	throw new Error(`Unable to find registry type for ${intRef}`);
};

export function uniqueArray(values: any[]): any[] {
	const result = [];
	const map = new Map();
	for (const item of values) {
		const h = hash(JSON.stringify(item));
		if (!map.has(h)) {
			map.set(h, true); // set any value to Map
			result.push(item);
		}
	}
	return result;
}

export const isQualityCheckAutomatic = (qc: QualityCheck) => {
	switch (qualityCheckCode(qc)) {
		case WI_DOCK_BAT_TEST:
		case WI_DOCK_FIRMWARE_UPDATE:
		case WI_WIFI_TEST:
		case WI_DOCK_CHECK_RECORDINGS:
		case WI_FACTORY_RESET:
		case WI_BATTERY_HEALTH_CHECK:
		case WI_24_HR_TEST:
		case WI_24_HR_TEST_SERVICING:
			return true;
		default:
			return false;
	}
};

export const isScannableLotProduct = (ref?: string) => {
	switch (ref) {
		case BF_PACKAGED_DOUBLE_SIDED_ADHESIVE:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS:
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR:
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS:
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110:
		case BF_PACKAGED_EEG_ADHESIVE:
		case BF_PACKAGED_EEG_ADHESIVE_114:
		case BF_GLINET_GLX750_PSU:
			return true;
		default:
			return false;
	}
};

export const isPreBomQualityCheck = (qc: QualityCheck) => {
	switch (qualityCheckCode(qc)) {
		case WI_LINE_CLEARANCE_START:
		case WI_BUILD_DOCK:
		case WI_DOCK_PROVISIONING:
		case WI_CALIBRATE_DOT:
			return true;
		default:
			return false;
	}
};

export const isScrappableQualityCheck = (qc: QualityCheck) => {
	switch (qualityCheckCode(qc)) {
		case WI_CALIBRATE_DOT:
		case WI_24_HR_TEST:
		case WI_24_HR_TEST_SERVICING:
		case WI_BATTERY_HEALTH_CHECK:
		case WI_WRITE_NFC_TAG:
		case WI_BUILD_DOT:
		case WI_BUILD_NAKED_ECG_3SNAP_CRADLE:
		case WI_DOT_VISUAL_INSPECTION_POGOPINS:
		case WI_DOT_VISUAL_INSPECTION_CRACKS:
		case WI_DOT_VISUAL_INSPECTION_WELD_SEAM:
		case WI_DOCK_VISUAL_INSPECTION_INTERFACE:
		case WI_DOCK_VISUAL_INSPECTION_CASING:
		case WI_CRADLE_VISUAL_INSPECTION_CONNECTORS:
		case WI_CRADLE_VISUAL_INSPECTION_POGOPADS:
		case WI_CRADLE_VISUAL_INSPECTION_CRACKS:
		case WI_CRADLE_VISUAL_INSPECTION_WELD_SEAM:
			return true;
		default:
			return false;
	}
};

export function sortQualityChecks(
	productInternalReference: string,
	checks: QualityCheck[],
): QualityCheck[] {
	if (isBlank(productInternalReference)) {
		return [];
	} else if (
		checks === undefined ||
		!Array.isArray(checks) ||
		checks.length === 0
	) {
		return [];
	}

	let sorted: string[] = [];

	if (productInternalReference === BF_NAKED_DOT) {
		sorted = [
			WI_LINE_CLEARANCE_START,
			WI_SENSOR_DOT_UPLOAD_RECORDINGS,
			WI_BATTERY_HEALTH_CHECK,
			WI_BUILD_DOT,
			WI_WRITE_NFC_TAG,
			WI_VISUAL_INSPECTION,
			WI_DOT_VISUAL_INSPECTION_POGOPINS,
			WI_DOT_VISUAL_INSPECTION_CRACKS,
			WI_DOT_VISUAL_INSPECTION_WELD_SEAM,
			WI_DOT_VISUAL_INSPECTION,
			WI_DOT_SIGNAL_QUALITY,
			WI_CALIBRATE_DOT,
			WI_DOT_EXTERNAL_CLEANING,
			WI_SENSOR_DOT_HW_TEST,
			WI_CHARGE_SENSOR_DOT,
			WI_24_HR_TEST,
			WI_24_HR_TEST_SERVICING,
			WI_PACKAGE_DOT,
			WI_CONFIGURE_ECG_DOTS,
			WI_LINE_CLEARANCE_END,
		];
	} else {
		sorted = [
			WI_LINE_CLEARANCE_START,
			WI_BATTERY_HEALTH_CHECK,
			WI_VISUAL_INSPECTION,
			WI_CRADLE_VISUAL_INSPECTION_CONNECTORS,
			WI_CRADLE_VISUAL_INSPECTION_POGOPADS,
			WI_CRADLE_VISUAL_INSPECTION_CRACKS,
			WI_CRADLE_VISUAL_INSPECTION_WELD_SEAM,
			WI_CRADLE_VISUAL_INSPECTION,
			WI_CLEAN_CRADLE_CONNECTORS,
			WI_DOCK_TEST,
			WI_PRINT_LABEL,
			WI_BUILD_DOT,
			WI_DOT_SIGNAL_QUALITY,
			WI_CALIBRATE_DOT,
			WI_24_HR_TEST,
			WI_24_HR_TEST_SERVICING,
			WI_PACKAGE_DOT,
			WI_BUILD_DOCK,
			WI_BUILD_NAKED_4WIRE_CRADLE,
			WI_TEST_4WIRE_CRADLE_WITH_FILTER,
			WI_TEST_4WIRE_CRADLE,
			WI_PACKAGE_4WIRE_CRADLE,
			WI_BUILD_NAKED_2SNAP_CRADLE,
			WI_TEST_2SNAP_CRADLE,
			WI_PACKAGE_2SNAP_CRADLE,
			WI_BUILD_NAKED_MOTION_CRADLE,
			WI_TEST_MOTION_CRADLE,
			WI_PACKAGE_ADHESIVE,
			WI_PACKAGE_ECG_ADHESIVE,
			WI_PACKAGE_EEG_ADHESIVE,
			WI_BUILD_NAKED_4SNAP_CRADLE,
			WI_TEST_4SNAP_CRADLE,
			WI_PACKAGE_4SNAP_CRADLE,
			WI_BUILD_NAKED_3SNAP_CRADLE_LEFT,
			WI_TEST_3SNAP_CRADLE_LEFT,
			WI_PACKAGE_3SNAP_CRADLE_LEFT,
			WI_BUILD_NAKED_3SNAP_CRADLE_RIGHT,
			WI_TEST_3SNAP_CRADLE_RIGHT,
			WI_PACKAGE_3SNAP_CRADLE_RIGHT,
			WI_CONFIGURE_ECG_DOTS,
			WI_PACKAGE_CARDIO_BOX,
			WI_CONFIGURE_EEG_DOTS,
			WI_CONFIGURE_ECG_DOTS_EPICARE,
			WI_CONFIGURE_EPICARE_FOCAL_BOX_DOTS,
			WI_PACKAGE_EPICARE_ABSCENCE_BOX,
			WI_CONFIGURE_ECG_DOTS_DUAL_CHANNEL,
			WI_DOCK_CLEANING,
			WI_DOCK_VISUAL_INSPECTION_INTERFACE,
			WI_DOCK_VISUAL_INSPECTION_SCREWS,
			WI_DOCK_VISUAL_INSPECTION_CASING,
			WI_DOCK_VISUAL_INSPECTION_BUMPER_FEET,
			WI_DOCK_VISUAL_INSPECTION,
			WI_DOCK_INTERFACE_TEST,
			WI_DOCK_BAT_TEST,
			WI_WIFI_TEST,
			WI_DOCK_FIRMWARE_UPDATE,
			WI_DOCK_CHECK_RECORDINGS,
			WI_FACTORY_RESET,
			WI_PRINT_LABEL_DOCK_SERVICING,
			WI_WRITE_NFC_TAG,
			WI_BUILD_NAKED_ECG_3SNAP_CRADLE,
			WI_PACKAGE_CARDIO_MULTI_LEAD_LINEAR_BOX,
			WI_4WIRESNAP_TEST_WITH_DEVICE,
			WI_BUILD_24H_HOLTER,
			WI_CONNECT_DOCK_MOBILE_WIFI,
			WI_PAIR_AD_MEDICAL_UA656BLE,
			WI_BUILD_VITAL_SIGNS_VS1,
			WI_BUILD_VITAL_SIGNS_VS2,
			WI_BUILD_VITAL_SIGNS_VS3,
			WI_BUILD_VITAL_SIGNS_VS4,
			WI_BUILD_24H_STARTER_KIT,
			WI_THIRD_PARTY_DEVICE_VISUAL_INSPECTION,
			WI_THIRD_PARTY_READABILITY_LABEL,
			WI_THIRD_PARTY_CHECK_ACCESSORIES,
			WI_THIRD_PARTY_CHECK_BATTERY,
			WI_THIRD_PARTY_DEVICE_CONFIGURE,
			WI_THIRD_PARTY_DEVICE_PROPER_FUNCTIONING,
			WI_THIRD_PARTY_DEVICE_FACTORY_RESET,
			WI_THIRD_PARTY_DEVICE_CLEANING,
			WI_THIRD_PARTY_PROTECTIVE_PACKAGING,
			WI_TEST_CRADLE_RESISTANCES,
			WI_ROUTER_TURN_OFF,
			WI_LINE_CLEARANCE_END,
		];
	}

	return checks.sort((a, b) => {
		const aCode = qualityCheckCode(a);
		if (isBlank(aCode)) {
			return -1;
		}
		const bCode = qualityCheckCode(b);
		if (isBlank(bCode)) {
			return 1;
		}
		const aIndex = sorted.indexOf(aCode.toUpperCase());
		const bIndex = sorted.indexOf(bCode.toUpperCase());
		if (aIndex === -1 || bIndex === -1) {
			return aCode.toUpperCase().localeCompare(bCode.toUpperCase());
		} else if (aIndex === bIndex) {
			return 0;
		} else if (aIndex > bIndex) {
			return 1;
		} else {
			return -1;
		}
	});
}

export function qualityCheckCode(qc: QualityCheck) {
	if (isBlank(qc.title)) {
		return "";
	}
	return substringAfter(qc.title!, " - ").trim();
}

export function qualityCheckCodeWithoutProject(qc: QualityCheck): string {
	return qualityCheckCode(qc).split("/").at(-1)!;
}

export function qualityCheckTitleWithoutCode(qc: QualityCheck) {
	if (isBlank(qc.title)) {
		return "";
	}
	return substringBefore(qc.title!, " - ").trim();
}

export function qualityPointCode(qc: QualityPoint) {
	if (isBlank(qc.title)) {
		return "";
	}
	const code = substringAfter(qc.title!, " - ");
	return code.trim();
}

export function matrixUrl(qc: QualityCheck): string | undefined {
	const code = qualityCheckCodeWithoutProject(qc);
	if (code === "") {
		return undefined;
	} else {
		return `https://byteflies.matrixreq.com/pub/QMS?show=${code}`;
	}
}

export function average(n: number[]): number | undefined {
	if (n === undefined || n.length === 0) {
		return undefined;
	} else if (n.length === 1) {
		return n[0];
	}

	let sum = 0;
	for (const v of n) {
		sum += v;
	}

	return sum / n.length;
}

export function max(n: number[]): number | undefined {
	if (n === undefined || n.length === 0) {
		return undefined;
	} else if (n.length === 1) {
		return n[0];
	}

	let m = n[0];
	for (const v of n) {
		if (v >= m) {
			m = v;
		}
	}

	return m;
}

export function min(n: number[]): number | undefined {
	if (n === undefined || n.length === 0) {
		return undefined;
	} else if (n.length === 1) {
		return n[0];
	}

	let m = n[0];
	for (const v of n) {
		if (v <= m) {
			m = v;
		}
	}

	return m;
}

// Hashes a string
function hash(str: string) {
	let hash = 0;

	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) - hash + str.charCodeAt(i)) & 0xffffffff;
	}

	return hash;
}

export function odooProductToRoutecardProduct(p: GetProduct) {
	const result: ExtendedProduct = {
		id: p.id,
		company_id: p.company_id === undefined ? -1 : id(p.company_id)!,
		internalReference: p.default_code,
		type: ProductType.StorableProduct,
		name: p.name,
		canBePurchased: false,
		canBeSold: true,
		inventory: {
			traceability: {
				tracking: odooTrackingToTracking(p.tracking),
			},
		},
		bom_ids:
			p.bom_ids !== undefined && Array.isArray(p.bom_ids)
				? (p.bom_ids as any as number[])
				: undefined,
		route_ids:
			p.route_ids !== undefined && Array.isArray(p.route_ids)
				? (p.route_ids as any as number[])
				: undefined,
	};
	return result;
}

export function odooTrackingToTracking(
	tracking: "lot" | "serial" | "none" | undefined,
) {
	switch (tracking) {
		case "lot":
			return Tracking.ByLots;
		case "serial":
			return Tracking.ByUniqueSerialNumber;
		case "none":
		default:
			return Tracking.NoTracking;
	}
}

export const getGtinByInternalReference = (internalReference: string) => {
	if (isBlank(internalReference)) {
		return undefined;
	}

	const gtins = products
		.filter((product) => {
			for (const i of product.internalReference) {
				if (i === internalReference) {
					return true;
				}
			}
			return false;
		})
		.map((product) => product.gtin);

	if (gtins.length === 1) {
		return gtins[0];
	}

	const uniqueArrays: string[] = uniqueArray(gtins);
	if (uniqueArrays !== undefined && uniqueArrays.length === 1) {
		return uniqueArrays[0];
	}

	return undefined;
};

export const getInternalReferencesByGtin = (gtin: string): string[] => {
	if (isBlank(gtin)) {
		return [];
	}

	const refs = products
		.filter((product) => product.gtin === gtin)
		.flatMap((product) => product.internalReference);

	const uniqueRefs: string[] = uniqueArray(refs);

	return uniqueRefs;
};

export const canCopySerialNumber = (
	sourceBfnumber: string | undefined,
	destinationBfnumber: string | undefined,
) => {
	if (isBlank(sourceBfnumber) || isBlank(destinationBfnumber)) {
		return false;
	} else if (
		sourceBfnumber === BF_NAKED_DOT &&
		destinationBfnumber === BF_PACKAGED_DOT
	) {
		return true;
	} else if (
		!isPackagedProduct(sourceBfnumber) &&
		isPackagedProduct(destinationBfnumber)
	) {
		return true;
	} else {
		return false;
	}
};

export const needsDeviceId = (bfnumber: string | undefined) => {
	if (isBlank(bfnumber)) {
		return true;
	} else if (
		bfnumber === BF_NAKED_DOT ||
		bfnumber === BF_PACKAGED_DOT ||
		bfnumber === BF_NAKED_DOCK
	) {
		return true;
	} else {
		return false;
	}
};

export const usesDeviceIdAsSerialNumber = (bfnumber: string | undefined) => {
	return false;
};

export function hasNfc(internalReference: string) {
	switch (internalReference) {
		case BF_NAKED_DOT:
		case BF_NAKED_2SNAPCRADLE:
		case BF_NAKED_2SNAPCRADLE_110:
		case BF_NAKED_3SNAPCRADLE_LEFT:
		case BF_NAKED_3SNAPCRADLE_RIGHT:
		case BF_NAKED_3WIRECRADLE_LEFT:
		case BF_NAKED_3WIRECRADLE_RIGHT:
		case BF_NAKED_4SNAPCRADLE:
		case BF_NAKED_ECG_3SNAPCRADLE:
		case BF_NAKED_MOTION_CRADLE:
		case BF_NAKED_4WIRE_CRADLE:
			return true;
		default:
			return false;
	}
}

export function hasLabel(internalReference: string) {
	if (isBlank(internalReference)) {
		return undefined;
	}
	switch (internalReference) {
		case BF_NAKED_DOCK:
		case BF_PACKAGED_DOT:
		case BF_PACKAGED_2SNAPCRADLE:
		case BF_PACKAGED_3SNAPCRADLE_LEFT:
		case BF_PACKAGED_3SNAPCRADLE_RIGHT:
		case BF_PACKAGED_4SNAPCRADLE:
		case BF_PACKAGED_ECG_3SNAPCRADLE:
		case BF_PACKAGED_MOTION_CRADLE:
		case BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX:
		case BF_PACKAGED_4WIRE_CRADLE:
		case BF_CARDIOCARE_LINEAR_BE_24H_HOLTER:
		case BF_CARDIOCARE_LINEAR_INTL_24H_HOLTER:
		case BF_CARDIOCARE_LINEAR_FR_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_BE_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_INTL_24H_HOLTER:
		case BF_CARDIOCARE_NON_LINEAR_FR_24H_HOLTER:
		case BF_CARDIOCARE_24H_STARTER_KIT:
		case BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER:
		case BF_VITAL_SIGNS_VS1:
		case BF_VITAL_SIGNS_VS2:
		case BF_VITAL_SIGNS_VS3:
		case BF_VITAL_SIGNS_VS4:
		case BF_VITAL_SIGNS_VS6:
		case BF_VITAL_SIGNS_VS7:
		case BF_VITAL_SIGNS_VS8:
		case BF_VITAL_SIGNS_VS9:
			return true;
		case BF_NAKED_DOT:
		case BF_NAKED_2SNAPCRADLE:
		case BF_NAKED_2SNAPCRADLE_110:
		case BF_NAKED_3SNAPCRADLE_LEFT:
		case BF_NAKED_3SNAPCRADLE_RIGHT:
		case BF_NAKED_3WIRECRADLE_LEFT:
		case BF_NAKED_3WIRECRADLE_RIGHT:
		case BF_NAKED_4SNAPCRADLE:
		case BF_NAKED_ECG_3SNAPCRADLE:
		case BF_NAKED_MOTION_CRADLE:
		case BF_NAKED_4WIRE_CRADLE:
			return false;
		default:
			return undefined;
	}
}

export const hasNfcWithId = (bfnumber: string | undefined) => {
	const devicesWithId = [BF_NAKED_DOCK, BF_NAKED_DOT];
	if (isBlank(bfnumber)) {
		throw new Error("BF-number should not be blank");
	} else if (devicesWithId.includes(bfnumber!.toUpperCase())) {
		return true;
	} else {
		return false;
	}
};

export const isPackagedProduct = (bfnumber: string | undefined) => {
	if (isBlank(bfnumber)) {
		return false;
	} else if (
		bfnumber === BF_PACKAGED_DOT ||
		bfnumber === BF_PACKAGED_2SNAPCRADLE ||
		bfnumber === BF_PACKAGED_ECG_3SNAPCRADLE ||
		bfnumber === BF_PACKAGED_3SNAPCRADLE_LEFT ||
		bfnumber === BF_PACKAGED_3SNAPCRADLE_RIGHT ||
		bfnumber === BF_PACKAGED_4SNAPCRADLE ||
		bfnumber === BF_PACKAGED_3WIRECRADLE_LEFT ||
		bfnumber === BF_PACKAGED_3WIRECRADLE_RIGHT ||
		bfnumber === BF_PACKAGED_4WIRE_CRADLE ||
		bfnumber === BF_PACKAGED_MOTION_CRADLE
	) {
		return true;
	} else {
		return false;
	}
};
